import Image from 'next/image'
import Link from 'next/link'
import { getReadableImageUrl } from "@/lib/directus";
import { sanitizeHTML } from "@/lib/utils";

interface DurationOfStayData {
  heading?: {
    text?: string;
    title?: string;
  };
  button?: {
    label?: string;
    href?: string;
  };
  image?: {
    alt?: string;
    image_file?: {
      image?: {
        id?: string;
        filename_download?: string;
      };
    };
  };
  content_collection?: {
    id: string;
    site_content_collections_id: {
      collection: {
        collection: string;
        item: {
          title: string;
          text: string;
        };
      }[];
    };
  }[];
}

export const fields = [
  // heading
  "heading.text",
  "heading.title",
  
  // button
  "button.label",
  "button.href",
  
  // image
  "image.alt",
  "image.image_file.image.id",
  "image.image_file.image.filename_download",
  
  // content collection
  "content_collection.id",
  "content_collection.site_content_collections_id.collection.collection",
  "content_collection.site_content_collections_id.collection.item:site_content.title",
  "content_collection.site_content_collections_id.collection.item:site_content.text",
] as const;

interface DurationOfStayProps {
  data?: DurationOfStayData;
}

export default function DurationOfStay({ data }: DurationOfStayProps) {
  const imageSrc = data?.image?.image_file?.image?.id && data?.image?.image_file?.image?.filename_download
    ? getReadableImageUrl(data.image.image_file.image.id, data.image.image_file.image.filename_download)
    : "/images/DurationOfStay.png";
  
  const imageAlt = data?.image?.alt || "Duration of Stay";

  return (
    <section className="w-full bg-white">
      <div className="w-full">
        {/* Main content wrapper */}
        <div className="flex flex-col md:flex-row">
          {/* Image section - Made responsive with aspect ratio instead of fixed height */}
          <div className="relative w-full md:w-1/2 aspect-[3/2] md:aspect-[1.5/1]">
            <Image
              src={imageSrc}
              alt={imageAlt}
              className="object-cover"
              fill
              sizes="(max-width: 768px) 100vw, 50vw"
              priority
              quality={90}
            />
          </div>

          {/* Content section - Centered with no horizontal padding */}
          <div className="w-full md:w-1/2 flex flex-col items-center py-9 md:py-[4.8125rem]">
            {/* Title - Centered */}
            <div className="max-w-[45rem] w-full px-8">
              <h2 className="text-[2rem] font-essonnes font-light leading-tight md:text-5xl lg:text-[3.375rem] text-[#193762]">
                {data?.heading?.title || ""}
              </h2>

              {/* Text content - Refined spacing and typography */}
              <div className="mt-[3.25rem] space-y-[3.25rem]">
                {data?.content_collection?.map((collection, index) => {
                  const contentItems = collection?.site_content_collections_id?.collection
                    .filter(item => item.collection === "site_content");
                  
                  return contentItems?.map((item, itemIndex) => (
                    <div key={`${index}-${itemIndex}`} className="space-y-6">
                      {item.item.title && (
                        <h3 className="text-xl md:text-[2rem] font-medium text-[#193762] font-greycliff leading-[1.36]">
                          {item.item.title}
                        </h3>
                      )}
                      {item.item.text && (
                        <div 
                          className="text-sm md:text-lg text-[#193762] font-greycliff leading-relaxed [&>p]:mb-4 [&>p:last-child]:mb-0"
                          dangerouslySetInnerHTML={sanitizeHTML(item.item.text)}
                        />
                      )}
                    </div>
                  ));
                })}
              </div>

              {/* CTA Button - Full width on mobile, auto width on desktop */}
              {data?.button?.href ? (
                <Link 
                  href={data.button.href || "#"}
                  className="mt-[3.25rem] inline-block w-full md:w-auto px-[3.3125rem] h-[4.375rem] bg-[#193762] text-white rounded-[3.125rem] border border-[#193762] text-xl font-greycliff hover:bg-opacity-90 transition-colors leading-[4.375rem] text-center"
                >
                  {data.button.label || "Book your consultation"}
                </Link>
              ) : (
                <button className="mt-[3.25rem] w-full md:w-auto px-[3.3125rem] h-[4.375rem] bg-[#193762] text-white rounded-[3.125rem] border border-[#193762] text-xl font-greycliff hover:bg-opacity-90 transition-colors">
                  Book your consultation
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
} 